/**
 * The longest single job title a people search may send (PREC-4987).
 *
 * Company Enrich refuses a person search whose `positionQuery` holds an entry over 250
 * characters: HTTP 422 `{"errors":{"positionquery":["Each position query can be at most 250
 * characters"]}}`. A persona's job titles become those entries one by one, so a persona saved with
 * one 2,037-character block of pasted titles made every company of the lists that search with it
 * fail, run after run. The persona editor quotes this number, the score save refuses a new or
 * edited title over it, and the people-search request is never built with one. The provider counts
 * UTF-16 code units (.NET string length), which is what `String.length` counts too.
 */
export const PERSON_SEARCH_TITLE_MAX_LENGTH = 250;

/** The independent entry cap for each CompanyEnrich include/exclude title list (PREC-5039). */
export const PERSON_SEARCH_TITLE_MAX_COUNT = 100;

/** Whether the final title list exceeds the provider's entry cap. */
export function isPersonSearchTitleCountTooHigh(
	titles: readonly string[] | null | undefined
): boolean {
	return (titles?.length ?? 0) > PERSON_SEARCH_TITLE_MAX_COUNT;
}

/** Legacy excessive lists may stay unchanged or shrink; a new or growing one cannot be saved. */
export function personSearchTitleCountBlocksSave(
	stored: readonly string[] | null | undefined,
	next: readonly string[] | null | undefined
): boolean {
	return isPersonSearchTitleCountTooHigh(next) && (next?.length ?? 0) > (stored?.length ?? 0);
}

/** How many characters of an over-long title a message quotes before the ellipsis. */
export const PERSON_SEARCH_TITLE_PREVIEW_LENGTH = 40;

/** Whether one job title is longer than a people search accepts. */
export function isPersonSearchTitleTooLong(title: string | null | undefined): boolean {
	return typeof title === "string" && title.length > PERSON_SEARCH_TITLE_MAX_LENGTH;
}

/**
 * The start of a title for a message: its first 40 characters and "…" when it is longer. Counted
 * in code points, so an emoji is never cut in half.
 */
export function personSearchTitlePreview(title: string): string {
	const characters = Array.from(title);

	return characters.length > PERSON_SEARCH_TITLE_PREVIEW_LENGTH
		? `${characters.slice(0, PERSON_SEARCH_TITLE_PREVIEW_LENGTH).join("")}…`
		: title;
}

/** An entry of a title list that is over the limit, and where it is. */
export interface OverLongPersonSearchTitle {
	index: number;
	title: string;
}

/** Every entry of a title list that is longer than a people search accepts, in order. */
export function overLongPersonSearchTitles(
	titles: readonly (string | null | undefined)[] | null | undefined
): OverLongPersonSearchTitle[] {
	return (titles ?? []).flatMap((title, index) =>
		isPersonSearchTitleTooLong(title) ? [{ index, title: title as string }] : []
	);
}

/**
 * The over-long titles a save would write that the stored list does not already hold (PREC-4987).
 * A save carries every persona of the score, so refusing an over-long title the persona already
 * had would block every unrelated edit, the way a legacy blank segment did; such a title is left as
 * it is (the people search refuses it on its own). A new persona has no stored list: all of its
 * titles are new.
 */
export function newOverLongPersonSearchTitles(
	stored: readonly string[] | null | undefined,
	next: readonly (string | null | undefined)[] | null | undefined
): OverLongPersonSearchTitle[] {
	const kept = new Set(stored ?? []);

	return overLongPersonSearchTitles(next).filter(({ title }) => !kept.has(title));
}

/** "the job title "Recruiter Senior Recruiter Technical Re…" is over 250 characters". */
export function overLongPersonSearchTitleText(title: string): string {
	return `the job title "${personSearchTitlePreview(title)}" is over ${PERSON_SEARCH_TITLE_MAX_LENGTH} characters`;
}
